import { ChangeDetectionStrategy, Component, ElementRef, inject, NgZone, viewChild, AfterViewInit, OnDestroy, DestroyRef } from '@angular/core';
import { assertNonNull, DeviceOrientationManager } from '@golf-ar/core';
import { filter, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SceneService } from '../../../services/scene.service';

/** Scene component. */
@Component({
	selector: 'gaw-scene',
	templateUrl: './scene.component.html',
	styleUrls: ['./scene.component.css'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SceneComponent implements AfterViewInit, OnDestroy {
	/** Canvas element. */
	public readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');

	private readonly zone = inject(NgZone);

	private readonly sceneService = inject(SceneService);

	private readonly abortController = new AbortController();

	private readonly destroyRef = inject(DestroyRef);

	/** @inheritdoc */
	public ngAfterViewInit(): void {
		this.sceneService.isSceneInitialized$.pipe(
			filter(isSceneInitialized => isSceneInitialized === true),
			tap(() => {
				window.addEventListener(
					'resize', this.sceneService.sceneInstance.onResize, { signal: this.abortController.signal },
				);

				// Register device orientation changes for proper resizing
				/** @see {@link DeviceOrientationManager.registerDeviceOrientationChange}. */
				DeviceOrientationManager.registerDeviceOrientationChange(this.sceneService.sceneInstance.onResize);
			}),
			takeUntilDestroyed(this.destroyRef),
		).subscribe();

		// Initialize the scene directly without XR
		this.zone.runOutsideAngular(() => {
			const canvasElement = this.canvas();
			assertNonNull(canvasElement);
			this.sceneService.initialize(canvasElement.nativeElement);
		});
	}

	/** @inheritdoc */
	public ngOnDestroy(): void {
		this.sceneService.disposeScene();
		this.abortController.abort();
	}
}
